import React from 'react';
import { useTranslation } from 'react-i18next';

const SpinnerIcon = () => (
  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

function AutoFillConfirmModal({
  open,
  shiftCount,
  inFlight,
  employeesWithDefault,
  totalEmployees,
  onKeepExisting,
  onReplaceAll,
  onCancel,
  onGoToTeam,
}) {
  const { t } = useTranslation('scheduler');

  if (!open) return null;

  const showPreflight =
    totalEmployees > 0 &&
    employeesWithDefault < totalEmployees * 0.5;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        aria-hidden="true"
        onClick={inFlight ? undefined : onCancel}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">

          {showPreflight && (
            <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              </svg>
              <div>
                <p>
                  {t('autoFill.preflightWarning', {
                    withDefault: employeesWithDefault,
                    total: totalEmployees,
                    skipped: totalEmployees - employeesWithDefault,
                  })}
                </p>
                {onGoToTeam && (
                  <button
                    type="button"
                    onClick={onGoToTeam}
                    className="mt-1 text-xs font-semibold text-amber-900 underline underline-offset-2 hover:no-underline focus:outline-none"
                  >
                    {t('autoFill.preflightAction')}
                  </button>
                )}
              </div>
            </div>
          )}

          <h2 className="text-base font-semibold text-gray-900 mb-2">
            {t('autoFill.confirmTitle')}
          </h2>
          <p className="text-sm text-gray-600 mb-5">
            {t('autoFill.confirmDescriptionHasShifts', { count: shiftCount })}
          </p>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={inFlight}
              onClick={onKeepExisting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {inFlight && <SpinnerIcon />}
              {t('autoFill.keepExisting')}
            </button>

            <button
              type="button"
              disabled={inFlight}
              onClick={onReplaceAll}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-orange-200 hover:from-orange-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {inFlight && <SpinnerIcon />}
              {t('autoFill.replaceAll')}
            </button>

            <button
              type="button"
              disabled={inFlight}
              onClick={onCancel}
              className="w-full rounded-lg px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('autoFill.cancel')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default AutoFillConfirmModal;
